import React, { useRef, useEffect, useState } from 'react';
import { useRetirementCalculator } from './hooks/useRetirementCalculator';
import StepSidebar from './components/StepSidebar';
import StepPersonalDetails from './components/StepPersonalDetails';
import StepPortfolio from './components/StepPortfolio';
import StepRetirementGoals from './components/StepRetirementGoals';
import ResultsDashboard from './components/ResultsDashboard';
import FrozenScenarioModal from './components/FrozenScenarioModal';
import AssumptionsModal from './components/AssumptionsModal';
import LoadScenarioModal from './components/LoadScenarioModal';
import './RetirementCalculatorApp.css';

const RetirementCalculatorApp = () => {
  const {
    currentStep,
    setStep,
    nextStep,
    prevStep,
    approach,
    setApproach,
    formData,
    updateFormData,
    results,
    frozenScenarios,
    isCalculating,
    error,
    availablePortfolios,
    riskUniverse,
    riskPortfolios,
    objectiveOptions,
    portfolioRegistry,
    selectedPercentileIndex,
    setSelectedPercentileIndex,
    calculate,
    freezeScenario,
    removeScenario,
    exportPdf,
    resetResults,
    stepOrder,
    stepDefs,
    // Database save/load
    savedScenarioId,
    isSaving,
    isLoadingScenario,
    loadedClientIdentifier,
    isScenarioLocked,
    saveToDatabase,
    loadFromDatabase,
    unlockScenario,
  } = useRetirementCalculator();

  const [viewingScenario, setViewingScenario] = useState(null);
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const resultsRef = useRef(null);
  const prevResultsRef = useRef(results);

  // Handler to load scenario from the modal
  const handleLoadScenario = async (scenarioId) => {
    await loadFromDatabase(scenarioId);
    setShowLoadModal(false);
  };

  // Auto-scroll to results when they first appear or update
  useEffect(() => {
    if (results && results !== prevResultsRef.current && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevResultsRef.current = results;
  }, [results]);

  const renderStep = () => {
    const stepKey = stepOrder[currentStep - 1];

    switch (stepKey) {
      case 'personal':
        return (
          <StepPersonalDetails
            formData={formData}
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
            isLocked={isScenarioLocked}
          />
        );
      case 'portfolio':
        return (
          <StepPortfolio
            approach={approach}
            setApproach={setApproach}
            formData={formData}
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
            availablePortfolios={availablePortfolios}
            riskUniverse={riskUniverse}
            riskPortfolios={riskPortfolios}
            objectiveOptions={objectiveOptions}
            portfolioRegistry={portfolioRegistry}
            isLocked={isScenarioLocked}
          />
        );
      case 'goals':
        return (
          <StepRetirementGoals
            formData={formData}
            updateFormData={updateFormData}
            prevStep={prevStep}
            onCalculate={calculate}
            isCalculating={isCalculating}
            isLocked={isScenarioLocked}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="retirement-app">
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="retirement-title mb-0">Retirement Income Calculator</h2>
          <div className="d-flex gap-2 align-items-center">
            <button
              className="btn btn-primary"
              onClick={() => setShowLoadModal(true)}
            >
              <i className="bi bi-folder2-open me-2"></i>
              Saved Scenarios
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setShowAssumptions(true)}
            >
              <i className="bi bi-gear me-1"></i>
              Model Assumptions
            </button>
          </div>
        </div>

        {/* Loaded client indicator */}
        {loadedClientIdentifier && (
          <div className={`alert ${isScenarioLocked ? 'alert-warning' : 'alert-info'} py-2 mb-4`}>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <i className={`bi ${isScenarioLocked ? 'bi-lock-fill' : 'bi-person-badge'} me-2`}></i>
                <strong>Loaded Client:</strong> {loadedClientIdentifier}
                {savedScenarioId && (
                  <span className="ms-3 text-muted">
                    <i className="bi bi-cloud-check me-1"></i>
                    Scenario ID: {savedScenarioId}
                  </span>
                )}
                {isScenarioLocked && (
                  <span className="badge bg-warning text-dark ms-3">
                    <i className="bi bi-eye me-1"></i>
                    View Only
                  </span>
                )}
              </div>
              <div className="d-flex gap-2">
                {isScenarioLocked && (
                  <button
                    className="btn btn-sm btn-warning"
                    onClick={unlockScenario}
                    title="Unlock to make changes (will save as a new scenario)"
                  >
                    <i className="bi bi-unlock me-1"></i>
                    Make Changes
                  </button>
                )}
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowLoadModal(true)}
                >
                  <i className="bi bi-arrow-repeat me-1"></i>
                  Load Different
                </button>
              </div>
            </div>
            {isScenarioLocked && (
              <div className="mt-2 small text-muted">
                <i className="bi bi-info-circle me-1"></i>
                This scenario is locked to preserve the original results. Click "Make Changes" to edit — changes will be saved as a new scenario.
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
          </div>
        )}

        <div className="row">
          {/* Sidebar */}
          <div className="col-lg-2 col-md-3 mb-4">
            <div style={{ position: 'sticky', top: '20px' }}>
              <StepSidebar
                currentStep={currentStep}
                setStep={setStep}
                stepOrder={stepOrder}
                stepDefs={stepDefs}
                frozenScenarios={frozenScenarios}
                removeScenario={removeScenario}
                onScenarioClick={(idx) => setViewingScenario(frozenScenarios[idx])}
                onCalculate={calculate}
                onReset={resetResults}
                onFreeze={freezeScenario}
                results={results}
                isCalculating={isCalculating}
                isLocked={isScenarioLocked}
              />
            </div>
          </div>

          {/* Main content */}
          <div className="col-lg-10 col-md-9">
            <div className="retirement-step-content">
              {renderStep()}
            </div>

            {/* Results below step content */}
            {results && (
              <div ref={resultsRef} className="mt-4">
                <ResultsDashboard
                  results={results}
                  formData={formData}
                  selectedPercentileIndex={selectedPercentileIndex}
                  setSelectedPercentileIndex={setSelectedPercentileIndex}
                  onExportPdf={exportPdf}
                  frozenScenarios={frozenScenarios}
                  portfolioRegistry={portfolioRegistry}
                  // Database save/load props
                  savedScenarioId={savedScenarioId}
                  isSaving={isSaving}
                  loadedClientIdentifier={loadedClientIdentifier}
                  isScenarioLocked={isScenarioLocked}
                  onSaveToDatabase={saveToDatabase}
                  onOpenLoadModal={() => setShowLoadModal(true)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <FrozenScenarioModal
        scenario={viewingScenario}
        isOpen={!!viewingScenario}
        onClose={() => setViewingScenario(null)}
      />
      <AssumptionsModal
        show={showAssumptions}
        onClose={() => setShowAssumptions(false)}
      />
      <LoadScenarioModal
        show={showLoadModal}
        onClose={() => setShowLoadModal(false)}
        onLoad={handleLoadScenario}
        currentScenarioId={savedScenarioId}
        isLoading={isLoadingScenario}
      />
    </div>
  );
};

export default RetirementCalculatorApp;
